import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/v2/ChatCard";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CLOSING_STATUS } from "constants/financialClosing";
import { ActionItemArchivedMessage } from "components/MessageBubble/ActionItemArchivedMessage";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ClosingPeriodWithVersion } from "pages/Books/FinancialClosing/ClosingPeriodWithVersion";
import {
  FinancialClosingDetail,
  useGetFinancialClosingQuery,
} from "store/apis/financialClosing";
import React, { ComponentProps } from "react";
import OpenItemsHeader from "static/images/OpenItemsHeader.svg";
import { FinancialReviewModal } from "pages/Books/FinancialClosing/FinancialReview/FinancialReviewModal";

type ReviewClosingCardProps = {
  customData: {
    action: string;
    card_type: string;
    end_date: string;
    entity_id: string;
    financial_closing_id: string;
    group_id: string;
    start_date: string;
    status: FinancialClosingDetail["status"];
  };
  isDeprecated: boolean;
  children?: React.ReactNode;
};

const isChangesRequestedStatus = (status: FinancialClosingDetail["status"]) =>
  status === CLOSING_STATUS.REJECTED ||
  status === CLOSING_STATUS.REQUEST_CHANGES;

const StatusTag = ({
  status,
}: {
  status: FinancialClosingDetail["status"];
}) => {
  if (status === CLOSING_STATUS.COMPLETED) {
    return (
      <Tag tagType="green" icon={false} size="small">
        Approved
      </Tag>
    );
  }

  if (isChangesRequestedStatus(status)) {
    return (
      <Tag tagType="red" icon={false} size="small">
        Changes required
      </Tag>
    );
  }

  if (status === CLOSING_STATUS.AMENDED) {
    return (
      <Tag tagType="gray" icon={false} size="small">
        Amended
      </Tag>
    );
  }

  return (
    <Tag tagType="orange" icon={false} size="small">
      Pending
    </Tag>
  );
};

const Action = ({
  status,
  itemId,
  entityId,
}: {
  status: FinancialClosingDetail["status"];
  itemId: string;
  entityId: string;
}) => {
  const reviewModal = useModal();
  const { isCustomer } = useRoleBasedView();

  const isReviewPending = status === CLOSING_STATUS.REVIEW_PENDING;

  if (isCustomer && isReviewPending) {
    return (
      <>
        <Button
          block
          customType="primary"
          size="small"
          onClick={reviewModal.open}
        >
          Review financials
        </Button>
        <FinancialReviewModal
          isOpen={reviewModal.isOpen}
          close={reviewModal.close}
          financialClosingId={itemId}
          entityId={entityId}
        />
      </>
    );
  }

  if (!isCustomer && isChangesRequestedStatus(status)) {
    return (
      <>
        <Button
          block
          customType="primary"
          size="small"
          onClick={reviewModal.open}
        >
          Review changes
        </Button>
        <FinancialReviewModal
          isOpen={reviewModal.isOpen}
          close={reviewModal.close}
          financialClosingId={itemId}
          entityId={entityId}
        />
      </>
    );
  }

  return (
    <>
      <Button
        block
        customType="secondary"
        size="small"
        onClick={reviewModal.open}
      >
        View details
      </Button>
      <FinancialReviewModal
        isOpen={reviewModal.isOpen}
        close={reviewModal.close}
        financialClosingId={itemId}
        entityId={entityId}
      />
    </>
  );
};

export const ReviewClosingCard = ({
  customData,
  isDeprecated,
  children,
}: ReviewClosingCardProps) => {
  const { entity_id: entityId, financial_closing_id: financialClosingId } =
    customData;

  const { data: financialClosing } = useGetFinancialClosingQuery(
    { entityId, financialClosingId },
    { skip: !entityId || !financialClosingId }
  );
  const status =
    financialClosing?.closing_card_details.status ?? customData.status;
  const isAmended = status === CLOSING_STATUS.AMENDED;

  return (
    <div className="t-min-w-64 t-mb-2">
      <ChatCard.Content addMargin={false}>
        <ChatCard.Header icon={<img src={OpenItemsHeader} alt="" />}>
          <div className="t-text-subtext-sm t-text-text-30">
            {customData.action}
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div className="t-space-y-4">
            <div className="t-flex t-items-center t-justify-between t-gap-2">
              <div className="t-text-subtext-sm t-text-text-60">
                <ClosingPeriodWithVersion
                  entityId={entityId}
                  financialClosingId={financialClosingId}
                  startDate={customData.start_date}
                  endDate={customData.end_date}
                />
              </div>
              <StatusTag status={status} />
            </div>
            {children && (
              <div className="t-text-subtext-sm t-text-text-60">{children}</div>
            )}
            {isDeprecated && !isAmended ? (
              <ActionItemArchivedMessage />
            ) : (
              <Action
                itemId={financialClosingId}
                status={status}
                entityId={entityId}
              />
            )}
          </div>
        </ChatCard.Body>
      </ChatCard.Content>
    </div>
  );
};
